<template>
  <div v-if="!inCuration">
    <div class="text-center">
      <h3 class="text-h6 font-weight-bold mb-3">
        <v-icon medium class="mr-2">fal chart-pie</v-icon>
        Photo evaluation
      </h3>
      <div v-if="availableFrom">
        <p class="text-body-1">
          This photo has been curated by
          <span class="bigger font-weight-bold">
            {{ photo.numberOfPeopleCurationClient }}
          </span>
          members
        </p>
        <v-dialog v-model="curationBreakdownDialog" width="400">
          <template #activator="{ on }">
            <v-btn depressed dark class="mb-3" v-on="on">
              <v-icon small class="mr-2">
                fal fa-chart-pie
              </v-icon>
              View breakdown
            </v-btn>
          </template>
          <v-card outlined>
            <v-card-title class="text-h5" primary-title>
              Curation breakdown
            </v-card-title>
            <v-card-text>
              <div v-if="!me">
                <p class="text-body-1">
                  Please login to see this information
                </p>
                <v-btn depressed dark to="/login" nuxt block>
                  Login
                </v-btn>
              </div>
              <div v-else-if="me && me.hasAnActiveSubscription">
                <Pie-chart
                  :data="breakdownData"
                  :options="pieChartOptions"
                  :height="200"
                />
              </div>
              <div v-else>
                <p class="text-body-1">
                  Upgrade to Pro to unlock this feature
                </p>
                <v-btn depressed dark to="/membership" nuxt block>
                  Get Pro
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </div>
    </div>
    <div
      v-if="
        photo.averageRating &&
          photo.numberOfPeopleCuration &&
          photo.numberOfPeopleCuration >= 3
      "
    >
      <v-container class="py-6 text-center">
        <v-row justify="center">
          <v-col cols="12" sm="6" md="5" lg="4" xl="3">
            <bar-chart
              :data="barChartdata"
              :options="barChartOptions"
              :height="300"
              :gradient="true"
            />
          </v-col>
          <v-col cols="12" sm="6" md="5" lg="4" xl="3">
            <radar-chart
              :data="radarChartData"
              :options="radarChartOptions"
              :height="300"
              :gradient="true"
            />
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col cols="12" sm="8" md="6" lg="4" xl="3">
            <div class="gradient"></div>
            <div class="d-flex justify-space-between">
              <div class="text-caption">0</div>
              <div class="text-caption">5</div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <div v-else>
      <p class="mt-4 mb-8 text-center">
        <v-icon small class="mr-1">fal info-circle</v-icon>
        Photo evaluation stats will show when enough data are available
      </p>
    </div>
  </div>
</template>

<script>
import each from 'lodash/each'
import moment from 'moment'
import { mapState } from 'vuex'
import BarChart from '@/components/common/charts/BarChart.vue'
import RadarChart from '@/components/common/charts/RadarChart.vue'
import PieChart from '@/components/common/charts/PieChart.vue'
import types from '@/utils/types'

export default {
  name: 'PhotoStats',
  components: {
    BarChart,
    RadarChart,
    PieChart
  },
  props: {
    me: {
      type: [Object, Boolean],
      default: () => {}
    },
    photo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      labels: ['Composition', 'Technique', 'Creativity', 'Content', 'Lighting'],
      barChartData: undefined,
      curationBreakdownDialog: false,
      pieChartOptions: {
        legend: {
          position: 'left'
        }
      },
      loadingCurationBreakdown: false
    }
  },
  computed: {
    ...mapState({
      curationBreakdown: state => state.photos.curationBreakdown
    }),
    breakdownData() {
      const labels = []
      const data = []
      const backgroundColor = []
      const backgroundMapper = {
        User: '#FFCCBC',
        Scholar: '#CFD8DC',
        Student: '#FFF176',
        Photographer: '#AED581',
        Guru: '#A5D6A7',
        Master: '#80CBC4',
        Curator: '#81D4FA'
      }
      each(this.curationBreakdown, b => {
        labels.push(b.roleName)
        backgroundColor.push(backgroundMapper[b.roleName])
        data.push(b.total)
      })
      const chartData = {
        labels,
        datasets: [
          {
            backgroundColor,
            data,
            borderWidth: 1
          }
        ]
      }
      return chartData
    },
    availableFrom() {
      return moment(this.photo.uploadDate).isAfter('2020-05-23T00:00:00')
    },
    inCuration() {
      return this.photo.statusName === types.photo.status.inCuration
    },
    barChartOptions() {
      const values = [
        this.photo.compositionRating,
        this.photo.techniqueRating,
        this.photo.creativityRating,
        this.photo.contentRating,
        this.photo.lightingRating
      ]
      const maxValue = Math.max(...values)
      const minValue = Math.min(...values)
      const max = maxValue + 0.2 > 5 ? 5 : maxValue + 0.2
      const min = minValue - 0.2 < 0 ? 0 : minValue - 0.2
      return {
        legend: {
          display: false
        },
        scales: {
          yAxes: [
            {
              gridLines: {
                color: this.$vuetify.theme.dark
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.1)'
              },
              ticks: {
                beginAtZero: true,
                min,
                max,
                stepSize: 0.5
              }
            }
          ],
          xAxes: [
            {
              gridLines: {
                color: this.$vuetify.theme.dark
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.1)'
              }
            }
          ]
        }
      }
    },
    barChartdata() {
      return {
        labels: this.labels,
        datasets: [
          {
            label: 'Evaluation',
            data: [
              this.photo.compositionRating,
              this.photo.techniqueRating,
              this.photo.creativityRating,
              this.photo.contentRating,
              this.photo.lightingRating
            ],
            borderWidth: 1
          }
        ]
      }
    },
    radarChartOptions() {
      const values = [
        this.photo.compositionRating,
        this.photo.techniqueRating,
        this.photo.creativityRating,
        this.photo.contentRating,
        this.photo.lightingRating
      ]
      const maxValue = Math.max(...values)
      const minValue = Math.min(...values)
      const max = maxValue + 0.2 > 5 ? 5 : maxValue + 0.2
      const min = minValue - 0.2 < 0 ? 0 : minValue - 0.2
      return {
        legend: false,
        scale: {
          angleLines: {
            display: false
          },
          gridLines: {
            color: this.$vuetify.theme.dark
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            showLabelBackdrop: false,
            min,
            max,
            stepSize: 0.25
          }
        }
      }
    },
    radarChartData() {
      return {
        labels: this.labels,
        datasets: [
          {
            label: 'Curation',
            backgroundColor: 'rgba(1, 160, 255, 0.5)',
            data: [
              this.photo.compositionRating,
              this.photo.techniqueRating,
              this.photo.creativityRating,
              this.photo.contentRating,
              this.photo.lightingRating
            ]
          }
        ]
      }
    }
  },
  watch: {
    curationBreakdownDialog(isVisible) {
      if (isVisible && this.me && this.me.hasAnActiveSubscription) {
        this.getCurationBreakdown()
      }
    }
  },
  methods: {
    async getCurationBreakdown() {
      try {
        this.loadingCurationBreakdown = true
        await this.$store.dispatch('photos/getCurationBreakdown', this.photo.id)
      } catch (error) {
        this.$asa.appInsights('Comp: photo/Stats.vue - method: getCurationBreakdown', error)
      } finally {
        this.loadingCurationBreakdown = false
      }
    }
  }
}
</script>

<style scoped>
.bigger {
  font-size: 130%;
}
.legend {
  display: flex;
  max-width: 400px;
}
.gradient {
  background: linear-gradient(
    90deg,
    rgba(225, 245, 254, 0.7) 0%,
    rgba(1, 87, 155, 0.7) 100%
  );
  width: 100%;
  height: 5px;
}
</style>
